const KIND_TYPES = new Set(["equipment", "ability"]);
const BODY_SLOTS = ["weapon", "armor", "trinket"];
const MISSING_SLOT_SCORE = 40;
const QUEST_EQUIPMENT_SLOTS = ["weapon", "head", "hands", "feet", "body", "trinket"];
const QUEST_SLOT_LABELS = {
  weapon: "Weapon",
  head: "Head",
  hands: "Hands",
  feet: "Feet",
  body: "Body",
  trinket: "Trinket",
};
const MISSING_QUEST_SLOT_SCORE = 24;
const SUPPORT_TARGET = 2;
const PRIMARY_ATTRIBUTES = ["strength", "dexterity", "wisdom"];
const CARD_ROLES = [
  "damage",
  "healing",
  "defense",
  "ailment",
  "resource",
  "draw",
  "minion",
];
const SUPPORT_ROLES = CARD_ROLES.filter((role) => role !== "damage");
const ROLE_LABELS = {
  damage: "damage",
  healing: "healing",
  defense: "defense",
  ailment: "ailments",
  resource: "resource generation",
  draw: "card draw",
  minion: "minions",
};
const DAMAGE_ENTRY_IDS = new Set([
  "damageOpponent",
  "attackFromIdentity",
  "bleed",
  "poison",
  "convertPhysicalArmorToDamage",
  "convertMagicArmorToDamage",
]);
const ENTRY_ROLES = {
  damageOpponent: "damage",
  attackFromIdentity: "damage",
  convertPhysicalArmorToDamage: "damage",
  convertMagicArmorToDamage: "damage",
  meleeWeaponDamageAdd: "damage",
  temporarySlottedAbilityDamageAdd: "damage",
  fletch: "damage",
  healSelf: "healing",
  regeneration: "healing",
  maxHealthIncrease: "healing",
  temporaryPhysicalArmor: "defense",
  temporaryMagicArmor: "defense",
  temporaryArmorFromIdentity: "defense",
  physicalArmor: "defense",
  magicArmor: "defense",
  dodgeRoll: "defense",
  gainThorns: "defense",
  temporaryBleedResistance: "defense",
  temporaryColdResistance: "defense",
  temporaryFireResistance: "defense",
  temporaryLightningResistance: "defense",
  burn: "ailment",
  chill: "ailment",
  shock: "ailment",
  poison: "ailment",
  bleed: "ailment",
  wither: "ailment",
  applyAilment: "ailment",
  ailmentFromIdentity: "ailment",
  temporarySlottedAbilityPoisonAdd: "ailment",
  hexExpendCost: "ailment",
  grantResources: "resource",
  gainResources: "resource",
  persist: "resource",
  convertResource: "resource",
  enhanceNextResourceExpend: "resource",
  grantSlottedAttributeResource: "resource",
  spendChargesForResources: "resource",
  nextActiveAbilityCostReduce: "resource",
  nextAttrCostReduce: "resource",
  nextWeaponExpendCostReduce: "resource",
  temporarySlottedAbilityCostReduce: "resource",
  draw: "draw",
  restock: "draw",
  discardSelfThenDraw: "draw",
  returnToEquipmentDeck: "draw",
  fromDiscard: "draw",
  summonMinion: "minion",
};
const PASSIVE_ROLES = {
  maxHealthAdd: "healing",
  physicalArmor: "defense",
  magicArmor: "defense",
  parry: "defense",
  turnStartThorns: "defense",
  slottedAbilityCostReduce: "resource",
  slottedActiveDamageAdd: "damage",
  hone: "damage",
  sharpen: "damage",
  armorBreaker: "damage",
};
const MODIFY_ROLES = {
  damageAdd: "damage",
  physicalDamageAdd: "damage",
  magicalDamageAdd: "damage",
  fireDamageAdd: "damage",
  coldDamageAdd: "damage",
  lightningDamageAdd: "damage",
  ignoreArmor: "damage",
  healAdd: "healing",
  physicalArmorAdd: "defense",
  magicalArmorAdd: "defense",
  burnAdd: "ailment",
  chillAdd: "ailment",
  shockAdd: "ailment",
  bleedAdd: "ailment",
  poisonAdd: "ailment",
  primaryCostReduce: "resource",
  minionHealthAdd: "minion",
};
// Item-kind tags (Sword, Mail, Ring, …). Tier 2+ equipment can only be played
// onto an equipped piece one tier lower sharing one of these (mirrors
// canUpgradeEquipment in server/AeternaInvicta/cardConstants.js).
const EQUIPMENT_ITEM_TYPES = new Set([
  "sword", "axe", "mace", "dagger", "club", "wand", "dart", "staff", "hammer",
  "bow", "crossbow", "launcher", "polearm", "hook", "throwable", "banner",
  "rod", "quiver", "artifact", "shield", "helmet", "hat", "crown", "cap",
  "circlet", "hood", "robes", "cuirass", "cloak", "mail", "coat", "vest",
  "bracers", "gloves", "gauntlets", "handWraps", "sandals", "boots", "greaves",
  "sabatons", "ring", "necklace", "earrings", "headband",
]);
const UPGRADE_BASE_SCORE = 6;
const STRANDED_UPGRADE_SCORE = -40;
const ENABLES_UPGRADE_SCORE = 15;
const roleCache = new WeakMap();
const aspectEffectCache = new WeakMap();

function resolveCardBoardSlot(card) {
  if (!card) return null;
  if (
    card.boardSlot === "weapon" ||
    card.boardSlot === "armor" ||
    card.boardSlot === "trinket"
  ) {
    return card.boardSlot;
  }
  const slot = card.slot;
  if (
    slot === "oneHandedWeapon" ||
    slot === "twoHandedWeapon" ||
    slot === "weapon"
  ) {
    return "weapon";
  }
  if (slot === "trinket") return "trinket";
  if (
    slot === "offHand" ||
    slot === "head" ||
    slot === "hands" ||
    slot === "feet" ||
    slot === "body" ||
    slot === "armor"
  ) {
    return "armor";
  }
  return null;
}

function cardHasType(card, type) {
  if (!card) return false;
  if (Array.isArray(card.type)) return card.type.includes(type);
  return card.type === type;
}

function equipmentTier(card) {
  const tier = Math.floor(Number(card?.tier) || 0);
  return tier >= 1 ? tier : 1;
}

function canUpgradeEquipmentCard(incoming, base) {
  if (!incoming || !base) return false;
  const tier = equipmentTier(incoming);
  if (tier < 2 || equipmentTier(base) !== tier - 1) return false;
  return (incoming.type || []).some(
    (type) => EQUIPMENT_ITEM_TYPES.has(type) && cardHasType(base, type),
  );
}

// Tier 2+ equipment in the list with no card to upgrade from (invoke only).
function findUnsupportedUpgrades(equipmentCards) {
  const cards = (equipmentCards || []).filter(Boolean);
  return cards.filter(
    (card) =>
      equipmentTier(card) >= 2 &&
      !cards.some((base) => canUpgradeEquipmentCard(card, base)),
  );
}

function cardAttributes(card) {
  if (!card) return [];
  if (Array.isArray(card.attributes) && card.attributes.length) {
    return card.attributes.filter(Boolean);
  }
  if (card.attribute) return [card.attribute];
  return [];
}

function cardSynergyTypes(card) {
  if (!card) return [];
  const types = Array.isArray(card.type)
    ? card.type
    : card.type
      ? [card.type]
      : [];
  return types.filter((type) => type && !KIND_TYPES.has(type));
}

function resolveQuestSlot(card) {
  if (!card || !cardHasType(card, "equipment")) return null;
  const slot = card.slot;
  if (slot === "oneHandedWeapon" || slot === "twoHandedWeapon") return "weapon";
  if (QUEST_EQUIPMENT_SLOTS.includes(slot)) return slot;
  return null;
}

function listMissingQuestSlots(questSlotCounts) {
  return QUEST_EQUIPMENT_SLOTS.filter((slot) => !(questSlotCounts?.[slot] > 0));
}

function fillsMissingQuestSlot(card, profile) {
  const questSlot = resolveQuestSlot(card);
  if (!questSlot || !profile) return false;
  return (profile.missingQuestSlots || []).includes(questSlot);
}

function formatLabel(value) {
  const text = String(value || "").replace(/([a-z])([A-Z])/g, "$1 $2");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function positiveAmount(value) {
  const amount = Math.floor(Number(value) || 0);
  return amount > 0 ? amount : 0;
}

function resolveCombatEffect(effect) {
  if (!effect) return null;
  if (effect.id === "summonMinion") return effect.action || null;
  if (effect.id === "activateAura") return null;
  return effect;
}

function listActivatedEffects(effect) {
  if (!effect) return [];
  if (Array.isArray(effect.choose)) return effect.choose.filter(Boolean);
  return Array.isArray(effect) ? effect : [effect];
}

function combatEntries(effect) {
  return listActivatedEffects(resolveCombatEffect(effect));
}

function flattenEffectEntries(effect, out = []) {
  if (!effect) return out;
  if (Array.isArray(effect)) {
    effect.forEach((entry) => flattenEffectEntries(entry, out));
    return out;
  }
  if (typeof effect !== "object") return out;
  if (Array.isArray(effect.choose)) {
    effect.choose.forEach((entry) => flattenEffectEntries(entry, out));
    return out;
  }
  if (effect.id === "summonMinion") {
    flattenEffectEntries(effect.action, out);
    return out;
  }
  if (effect.byIdentity && typeof effect.byIdentity === "object") {
    Object.values(effect.byIdentity).forEach((entry) =>
      flattenEffectEntries(entry, out),
    );
    return out;
  }
  out.push(effect);
  return out;
}

function convertStacksDealsDamage(entry) {
  return (
    entry?.id === "convertStacks" &&
    (entry.convert === "damage" || entry.convert === "damageOpponent")
  );
}

function entryDealsDamage(entry) {
  if (!entry || typeof entry !== "object") return false;
  return DAMAGE_ENTRY_IDS.has(entry.id) || convertStacksDealsDamage(entry);
}

function augmentTypeRules(matchRule) {
  const rules = [];
  if (!matchRule) return rules;
  if (Array.isArray(matchRule.type)) rules.push(...matchRule.type);
  else if (matchRule.type) rules.push(matchRule.type);
  if (Array.isArray(matchRule.types)) rules.push(...matchRule.types);
  else if (matchRule.types) rules.push(matchRule.types);
  return rules;
}

function effectMatchesCardTypes(card, match) {
  if (!match || typeof match !== "object") return true;
  if (Array.isArray(match.typesAny) && match.typesAny.length) {
    return match.typesAny.some((type) => cardHasType(card, type));
  }
  return augmentTypeRules(match).every((type) => cardHasType(card, type));
}

function cardOutputEntries(card) {
  if (!card) return [];
  if (cardHasType(card, "ability")) {
    return card.kind === "active" ? flattenEffectEntries(card.effect) : [];
  }
  if (cardHasType(card, "equipment")) {
    return flattenEffectEntries(card.activatedEffect);
  }
  return [];
}

function collectEffectRoles(effect, roles) {
  if (!effect) return;
  if (Array.isArray(effect)) {
    effect.forEach((entry) => collectEffectRoles(entry, roles));
    return;
  }
  if (typeof effect !== "object") return;
  if (Array.isArray(effect.choose)) {
    effect.choose.forEach((entry) => collectEffectRoles(entry, roles));
    return;
  }
  if (effect.byIdentity && typeof effect.byIdentity === "object") {
    Object.values(effect.byIdentity).forEach((entry) =>
      collectEffectRoles(entry, roles),
    );
  }
  if (effect.action) collectEffectRoles(effect.action, roles);
  if (convertStacksDealsDamage(effect)) roles.add("damage");
  if (ENTRY_ROLES[effect.id]) roles.add(ENTRY_ROLES[effect.id]);
  if (["burn", "chill", "shock"].some((stack) => Number(effect[stack]) > 0)) {
    roles.add("ailment");
  }
}

function collectModifyRoles(modify, roles) {
  Object.entries(modify || {}).forEach(([key, value]) => {
    if (MODIFY_ROLES[key] && value) roles.add(MODIFY_ROLES[key]);
  });
}

function cardRoles(card) {
  if (!card || typeof card !== "object") return [];
  if (roleCache.has(card)) return roleCache.get(card);
  const roles = new Set();
  if (cardHasType(card, "ability")) {
    collectEffectRoles(card.effect, roles);
    const augment = card.augment;
    if (augment) {
      collectModifyRoles(augment.modify, roles);
      (augment.modifyByLevel || []).forEach((modify) =>
        collectModifyRoles(modify, roles),
      );
    }
  }
  if (cardHasType(card, "equipment")) {
    collectEffectRoles(card.activatedEffect, roles);
    collectEffectRoles(card.onEquip, roles);
    (card.passiveEffects || []).forEach((effect) => {
      if (effect && PASSIVE_ROLES[effect.id]) roles.add(PASSIVE_ROLES[effect.id]);
    });
  }
  const ordered = CARD_ROLES.filter((role) => roles.has(role));
  roleCache.set(card, ordered);
  return ordered;
}

function cardSupportRoles(card) {
  return cardRoles(card).filter((role) => SUPPORT_ROLES.includes(role));
}

function isSupportCard(card) {
  return cardSupportRoles(card).length > 0;
}

function computeAspectEffects(aspect, card) {
  const benefits = [];
  const penalties = [];
  if (!aspect || !card) return { benefits, penalties };
  const name = aspect.name || "Aspect";
  const isAbility = cardHasType(card, "ability");
  const isEquipment = cardHasType(card, "equipment");
  const entries = cardOutputEntries(card);
  (aspect.passiveEffects || []).forEach((effect) => {
    if (!effect || typeof effect !== "object") return;
    const amount = positiveAmount(effect.amount);
    if (
      effect.id === "oncePerTurnOutgoingDamageAdd" ||
      effect.id === "outgoingDamageAdd"
    ) {
      if (!amount || !effectMatchesCardTypes(card, effect.match)) return;
      const wanted = effect.damageType || null;
      const deals = entries.some(
        (entry) =>
          entryDealsDamage(entry) &&
          (!wanted || (entry.damageType || "physical") === wanted),
      );
      if (!deals) return;
      const once = effect.id === "oncePerTurnOutgoingDamageAdd";
      benefits.push(
        `${name}: +${amount} ${wanted ? `${wanted} ` : ""}damage${
          once ? " once per turn" : ""
        }`,
      );
      return;
    }
    if (
      effect.id === "oncePerTurnPoisonStackAdd" ||
      effect.id === "oncePerTurnAilmentStackAdd"
    ) {
      const poison = effect.id === "oncePerTurnPoisonStackAdd";
      if (!amount || !entries.length) return;
      const matched = effect.match
        ? effectMatchesCardTypes(card, effect.match)
        : entries.some((entry) =>
            poison
              ? entry.id === "poison"
              : entry.id === "applyAilment" ||
                ["burn", "chill", "shock", "wither", "bleed", "poison"].includes(
                  entry.id,
                ),
          );
      if (!matched) return;
      benefits.push(
        `${name}: +${amount} ${poison ? "poison" : "ailment"} once per turn`,
      );
      return;
    }
    if (effect.id === "oncePerTurnAttrCostReduce") {
      const attribute = effect.attribute;
      if (!amount || !isAbility || !attribute) return;
      const minCost = Math.max(1, positiveAmount(effect.minCost));
      if (!(Number(card.cost?.[attribute]) >= minCost)) return;
      benefits.push(
        `${name}: -${amount} ${formatLabel(attribute)} cost once per turn`,
      );
      return;
    }
    if (effect.id === "turnEndConvertToPersistent") {
      if (!isAbility) return;
      const banked = PRIMARY_ATTRIBUTES.filter(
        (attribute) =>
          Number(effect.resources?.[attribute]) > 0 &&
          Number(card.cost?.[attribute]) > 0,
      );
      if (!banked.length) return;
      benefits.push(
        `${name} banks unspent ${banked.map(formatLabel).join("/")} toward its cost`,
      );
      return;
    }
    if (effect.id === "oncePerTurnOutgoingHealAdd") {
      if (!amount || !entries.some((entry) => entry.id === "healSelf")) return;
      benefits.push(`${name}: +${amount} healing once per turn`);
      return;
    }
    if (effect.id === "oncePerTurnPhysicalArmorGainAdd") {
      if (
        !amount ||
        !entries.some(
          (entry) =>
            entry.id === "physicalArmor" || entry.id === "temporaryPhysicalArmor",
        )
      ) {
        return;
      }
      benefits.push(`${name}: +${amount} physical armor once per turn`);
      return;
    }
    if (effect.id === "weaponSlotDamageAdd") {
      if (!amount || !isEquipment || !effect.slot) return;
      const slotMatch =
        card.slot === effect.slot ||
        resolveCardBoardSlot(card) === effect.slot ||
        cardHasType(card, effect.slot);
      if (!slotMatch || !entries.some(entryDealsDamage)) return;
      benefits.push(`${name}: +${amount} damage from this ${formatLabel(effect.slot)}`);
      return;
    }
    if (
      effect.id === "outgoingOutputMult" ||
      effect.id === "activeAbilityOutputMult"
    ) {
      const mult = Number(effect.mult);
      if (!Number.isFinite(mult) || !(mult > 0) || mult === 1) return;
      if (!entries.length) return;
      if (effect.id === "activeAbilityOutputMult" && card.kind !== "active") {
        return;
      }
      if (
        effect.id === "outgoingOutputMult" &&
        !effectMatchesCardTypes(card, effect.match)
      ) {
        return;
      }
      const percent = Math.round(Math.abs(1 - mult) * 100);
      if (mult < 1) penalties.push(`${name} cuts its output by ${percent}%`);
      else benefits.push(`${name} raises its output by ${percent}%`);
    }
  });
  return { benefits, penalties };
}

function aspectEffectsForCard(aspect, card) {
  if (!aspect || !card || typeof aspect !== "object" || typeof card !== "object") {
    return computeAspectEffects(aspect, card);
  }
  let byCard = aspectEffectCache.get(aspect);
  if (!byCard) {
    byCard = new WeakMap();
    aspectEffectCache.set(aspect, byCard);
  }
  if (!byCard.has(card)) byCard.set(card, computeAspectEffects(aspect, card));
  return byCard.get(card);
}

function isNeutralOnly(attributes) {
  return !attributes.length || attributes.every((attribute) => attribute === "neutral");
}

function aspectAffinity(aspect, card) {
  const effects = aspectEffectsForCard(aspect, card);
  if (!aspect || !card) {
    return { ...effects, sharedAttributes: [], synergy: false, compatible: true };
  }
  const aspectAttributes = cardAttributes(aspect);
  const attributes = cardAttributes(card);
  const sharedAttributes = attributes.filter((attribute) =>
    aspectAttributes.includes(attribute),
  );
  const synergy = effects.benefits.length > 0 || sharedAttributes.length > 0;
  const compatible =
    !effects.penalties.length &&
    (synergy || isNeutralOnly(attributes) || isNeutralOnly(aspectAttributes));
  return { ...effects, sharedAttributes, synergy, compatible };
}

function effectHealthCost(effect) {
  if (!effect) return 0;
  if (Array.isArray(effect)) {
    return effect.reduce((sum, entry) => sum + effectHealthCost(entry), 0);
  }
  if (Array.isArray(effect.choose)) {
    return effect.choose.reduce(
      (sum, entry) => sum + effectHealthCost(entry),
      0,
    );
  }
  if (effect.id === "damageSelf") {
    return Math.max(0, Number(effect.amount) || 0);
  }
  return 0;
}

function cardCostTotal(card) {
  if (!card) return 0;
  if (cardHasType(card, "ability")) {
    const resources = Object.values(card.cost || {}).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    );
    return resources + effectHealthCost(card.effect);
  }
  return Object.values(card.resources || {}).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );
}

function bumpCount(map, key, amount = 1) {
  if (!key) return;
  map[key] = (map[key] || 0) + amount;
}

function resolveCards(ids, getCard) {
  return (ids || [])
    .map((id) => (typeof id === "string" ? getCard(id) : id))
    .filter(Boolean);
}

function listMissingEquipmentSlots(slotCounts) {
  return BODY_SLOTS.filter((slot) => !(slotCounts?.[slot] > 0));
}

function buildDeckProfile(equipmentIds, abilityIds, getCard, aspect = null) {
  const equipmentCards = resolveCards(equipmentIds, getCard);
  const abilityCards = resolveCards(abilityIds, getCard);
  const allCards = [...equipmentCards, ...abilityCards];
  const aspectCard =
    typeof aspect === "string" ? getCard(aspect) || null : aspect || null;
  const attributeCounts = {};
  const typeCounts = {};
  const slotCounts = {};
  const questSlotCounts = {};
  const roleCounts = {};
  const supportCounts = { equipment: 0, ability: 0 };
  let costSum = 0;
  let activeCount = 0;
  let augmentCount = 0;
  let essenceCount = 0;

  allCards.forEach((card) => {
    cardAttributes(card).forEach((attribute) => {
      bumpCount(attributeCounts, attribute);
    });
    cardSynergyTypes(card).forEach((type) => {
      bumpCount(typeCounts, type);
    });
    const boardSlot = resolveCardBoardSlot(card);
    if (boardSlot) bumpCount(slotCounts, boardSlot);
    const questSlot = resolveQuestSlot(card);
    if (questSlot) bumpCount(questSlotCounts, questSlot);
    cardRoles(card).forEach((role) => bumpCount(roleCounts, role));
    if (isSupportCard(card)) {
      if (cardHasType(card, "equipment")) supportCounts.equipment += 1;
      else if (cardHasType(card, "ability")) supportCounts.ability += 1;
    }
    costSum += cardCostTotal(card);
    if (card.kind === "active") activeCount += 1;
    if (card.kind === "augment") augmentCount += 1;
    if (card.kind === "essence") essenceCount += 1;
  });

  const cardCount = allCards.length;
  const missingSlots =
    equipmentCards.length > 0 ? listMissingEquipmentSlots(slotCounts) : [];
  const missingQuestSlots =
    equipmentCards.length > 0 ? listMissingQuestSlots(questSlotCounts) : [];
  return {
    attributeCounts,
    typeCounts,
    slotCounts,
    missingSlots,
    questSlotCounts,
    missingQuestSlots,
    roleCounts,
    supportCounts,
    aspect: aspectCard,
    avgCost: cardCount ? costSum / cardCount : 0,
    activeCount,
    augmentCount,
    essenceCount,
    equipmentCards,
    unsupportedUpgrades: findUnsupportedUpgrades(equipmentCards),
    equipmentCount: equipmentCards.length,
    abilityCount: abilityCards.length,
    cardCount,
  };
}

function dominantAttributes(attributeCounts) {
  const entries = Object.entries(attributeCounts || {}).filter(
    ([, count]) => count > 0,
  );
  if (!entries.length) return [];
  const max = Math.max(...entries.map(([, count]) => count));
  return entries
    .filter(([, count]) => count === max)
    .map(([attribute]) => attribute);
}

function fillsMissingEquipmentSlot(card, profile) {
  const boardSlot = resolveCardBoardSlot(card);
  if (!boardSlot || !profile) return false;
  return (profile.missingSlots || []).includes(boardSlot);
}

function scoreRoleFit(card, profile, deckType) {
  const roles = cardRoles(card);
  const supportRoles = roles.filter((role) => SUPPORT_ROLES.includes(role));
  const supportCount = profile.supportCounts?.[deckType] || 0;
  const roleCounts = profile.roleCounts || {};
  if (supportRoles.length) {
    const newRoles = supportRoles.filter((role) => !(roleCounts[role] > 0)).length;
    return (supportCount < SUPPORT_TARGET ? 10 : 0) + 3 * Math.min(newRoles, 2);
  }
  if (roles.includes("damage") && supportCount < SUPPORT_TARGET) return -3;
  return 0;
}

function scoreAspectFit(card, profile) {
  if (!profile.aspect) return 0;
  const affinity = aspectAffinity(profile.aspect, card);
  let score = 0;
  if (affinity.sharedAttributes.length) score += 5;
  score += 6 * Math.min(affinity.benefits.length, 2);
  score -= 20 * affinity.penalties.length;
  if (!affinity.compatible && !affinity.penalties.length) score -= 4;
  return score;
}

function scoreCardAgainstProfile(card, profile, deckType) {
  if (!card || !profile || profile.cardCount <= 0) return 0;
  let score = 0;
  const attrs = cardAttributes(card);
  const dominant = dominantAttributes(profile.attributeCounts);

  attrs.forEach((attribute) => {
    const count = profile.attributeCounts[attribute] || 0;
    if (count > 0) {
      score += 4 + count * 2;
      if (dominant.includes(attribute)) score += 3;
    } else if (attribute === "neutral") {
      score += 1;
    } else {
      score -= 2;
    }
  });

  cardSynergyTypes(card).forEach((type) => {
    const count = profile.typeCounts[type] || 0;
    if (count > 0) score += 3 + count;
  });

  if (deckType === "equipment") {
    const boardSlot = resolveCardBoardSlot(card);
    if (boardSlot) {
      const slotCount = profile.slotCounts[boardSlot] || 0;
      if (slotCount === 0) score += MISSING_SLOT_SCORE;
      else if (slotCount === 1) score += 1;
      else score -= 4;
    }
    if (fillsMissingQuestSlot(card, profile)) score += MISSING_QUEST_SLOT_SCORE;
    if (equipmentTier(card) >= 2) {
      const hasBase = (profile.equipmentCards || []).some((base) =>
        canUpgradeEquipmentCard(card, base),
      );
      score += hasBase ? UPGRADE_BASE_SCORE : STRANDED_UPGRADE_SCORE;
    }
    if (
      (profile.unsupportedUpgrades || []).some((upgrade) =>
        canUpgradeEquipmentCard(upgrade, card),
      )
    ) {
      score += ENABLES_UPGRADE_SCORE;
    }
  }

  score += scoreRoleFit(card, profile, deckType);
  score += scoreAspectFit(card, profile);

  const cost = cardCostTotal(card);
  if (profile.avgCost >= 2) {
    if (cost <= 1) score += 4;
    else if (cost >= 3) score -= 2;
  } else if (profile.avgCost > 0 && profile.avgCost <= 1) {
    if (cost >= 2) score += 4;
    else if (cost === 0) score -= 1;
  } else if (Math.abs(cost - profile.avgCost) <= 1) {
    score += 1;
  }

  if (deckType === "ability" && card.kind) {
    const totalAbilities =
      profile.activeCount + profile.augmentCount + (profile.essenceCount || 0);
    if (totalAbilities === 0) {
      score += 2;
    } else {
      if (card.kind === "active") {
        if (profile.activeCount === 0) score += 6;
        else if (profile.activeCount < profile.augmentCount) score += 4;
        else if (profile.activeCount > profile.augmentCount + 2) score -= 2;
      }
      if (card.kind === "augment") {
        if (profile.augmentCount === 0) score += 6;
        else if (profile.augmentCount < profile.activeCount) score += 4;
        else if (profile.augmentCount > profile.activeCount + 2) score -= 2;
      }
      if (card.kind === "essence") {
        if ((profile.essenceCount || 0) === 0) score += 4;
        else if ((profile.essenceCount || 0) < 2) score += 2;
      }
    }
  }

  return score;
}

function suggestionKeepCount(candidateCount, cardCount) {
  if (candidateCount <= 0 || cardCount <= 0) return 0;
  const fillRatio = Math.min(1, cardCount / 20);
  const keepFraction = Math.max(0.12, 0.55 - fillRatio * 0.4);
  const minKeep = cardCount <= 3 ? 8 : cardCount <= 8 ? 6 : 4;
  return Math.min(
    candidateCount,
    Math.max(minKeep, Math.ceil(candidateCount * keepFraction)),
  );
}

function filterSuggestedCards(candidates, profile, deckType) {
  const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  if (!profile || profile.cardCount <= 0) return list;

  let pool = list;
  if (
    deckType === "equipment" &&
    (profile.missingSlots || []).length > 0 &&
    profile.equipmentCount > 0
  ) {
    const missingSlotCards = list.filter((card) =>
      fillsMissingEquipmentSlot(card, profile),
    );
    if (missingSlotCards.length) pool = missingSlotCards;
  }
  if (
    pool === list &&
    deckType === "equipment" &&
    (profile.missingQuestSlots || []).length > 0 &&
    profile.equipmentCount > 0
  ) {
    const missingQuestCards = list.filter((card) =>
      fillsMissingQuestSlot(card, profile),
    );
    if (missingQuestCards.length) pool = missingQuestCards;
  }

  const scored = pool
    .map((card) => ({
      card,
      score: scoreCardAgainstProfile(card, profile, deckType),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return String(a.card.name || "").localeCompare(String(b.card.name || ""));
    });

  const keepCount = suggestionKeepCount(scored.length, profile.cardCount);
  const topScore = scored[0]?.score || 0;
  const fillRatio = Math.min(1, profile.cardCount / 20);
  const minScore = Math.max(
    0,
    Math.floor(topScore * (0.25 + fillRatio * 0.35)),
  );

  return scored
    .filter((entry, index) => index < keepCount && entry.score >= minScore)
    .map((entry) => entry.card);
}

function canSuggestCards(equipmentIds, abilityIds) {
  const equipmentCount = (equipmentIds || []).filter(Boolean).length;
  const abilityCount = (abilityIds || []).filter(Boolean).length;
  return equipmentCount + abilityCount > 0;
}

export {
  EQUIPMENT_ITEM_TYPES,
  equipmentTier,
  canUpgradeEquipmentCard,
  findUnsupportedUpgrades,
  BODY_SLOTS,
  MISSING_SLOT_SCORE,
  QUEST_EQUIPMENT_SLOTS,
  QUEST_SLOT_LABELS,
  MISSING_QUEST_SLOT_SCORE,
  SUPPORT_TARGET,
  PRIMARY_ATTRIBUTES,
  CARD_ROLES,
  SUPPORT_ROLES,
  ROLE_LABELS,
  cardHasType,
  cardAttributes,
  cardSynergyTypes,
  cardCostTotal,
  resolveCardBoardSlot,
  resolveQuestSlot,
  listMissingEquipmentSlots,
  listMissingQuestSlots,
  fillsMissingEquipmentSlot,
  fillsMissingQuestSlot,
  formatLabel,
  positiveAmount,
  combatEntries,
  flattenEffectEntries,
  convertStacksDealsDamage,
  entryDealsDamage,
  augmentTypeRules,
  effectMatchesCardTypes,
  cardOutputEntries,
  cardRoles,
  cardSupportRoles,
  isSupportCard,
  aspectEffectsForCard,
  aspectAffinity,
  buildDeckProfile,
  scoreCardAgainstProfile,
  suggestionKeepCount,
  filterSuggestedCards,
  canSuggestCards,
  dominantAttributes,
};
